
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp, Calendar, DollarSign } from 'lucide-react';

interface MonthlySales {
  month: string;
  revenue: number;
  orders: number;
}

interface YearlySalesViewProps {
  restaurantId: string;
}

const YearlySalesView = ({ restaurantId }: YearlySalesViewProps) => {
  const [monthlySales, setMonthlySales] = useState<MonthlySales[]>([]);
  const [totalYearlyRevenue, setTotalYearlyRevenue] = useState(0);
  const [totalYearlyOrders, setTotalYearlyOrders] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchYearlySales();
  }, [restaurantId]);

  const fetchYearlySales = async () => {
    try {
      const currentYear = new Date().getFullYear();
      
      const { data, error } = await supabase
        .from('orders')
        .select('total_amount, delivery_fee, completed_at')
        .eq('restaurant_id', restaurantId)
        .gte('completed_at', `${currentYear}-01-01T00:00:00`)
        .lt('completed_at', `${currentYear + 1}-01-01T00:00:00`)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: true });

      if (error) {
        console.error('Error fetching yearly sales:', error);
        return;
      }

      // Group sales by month
      const monthlyData: { [key: string]: { revenue: number; orders: number } } = {};
      let yearlyRevenue = 0;
      let yearlyOrders = 0;

      data?.forEach(order => {
        const date = new Date(order.completed_at);
        const monthKey = date.toLocaleString('default', { month: 'long' });
        
        // Calculate vendor revenue (total - delivery fee - 10% commission)
        const orderTotal = Number(order.total_amount) - (Number(order.delivery_fee) || 0);
        const vendorRevenue = orderTotal * 0.90; // Keep 90%, platform takes 10%
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { revenue: 0, orders: 0 };
        }
        
        monthlyData[monthKey].revenue += vendorRevenue;
        monthlyData[monthKey].orders += 1;
        
        yearlyRevenue += vendorRevenue;
        yearlyOrders += 1;
      });

      // Convert to array format
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];

      const salesData = months.map(month => ({
        month,
        revenue: monthlyData[month]?.revenue || 0,
        orders: monthlyData[month]?.orders || 0
      }));

      setMonthlySales(salesData);
      setTotalYearlyRevenue(yearlyRevenue);
      setTotalYearlyOrders(yearlyOrders);
    } catch (error) {
      console.error('Error fetching yearly sales:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Your Revenue</p>
                <p className="text-2xl font-bold">D{totalYearlyRevenue.toFixed(2)}</p>
                <p className="text-xs text-gray-500">Your earnings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold">{totalYearlyOrders}</p>
                <p className="text-xs text-gray-500">Completed orders</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">Average per Order</p>
                <p className="text-2xl font-bold">
                  D{totalYearlyOrders > 0 ? (totalYearlyRevenue / totalYearlyOrders).toFixed(2) : '0.00'}
                </p>
                <p className="text-xs text-gray-500">Revenue per order</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Sales Breakdown ({new Date().getFullYear()})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {monthlySales.map((month) => (
              <div key={month.month} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium">{month.month}</h3>
                  <p className="text-sm text-gray-600">{month.orders} orders</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">D{month.revenue.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">
                    {month.orders > 0 ? `D${(month.revenue / month.orders).toFixed(2)} avg` : 'No orders'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default YearlySalesView;
